import { DateSelectOption } from '@gamma/ui/form-fields';
import { Auth, Hub } from 'aws-amplify';
import moment from 'moment';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useGetUserTcAcceptance } from '../features/user/queries';
import { dateRangeOptions, REGEX, ROUTES } from '../shared/constants';
import { buildLoginRedirectUrl } from '../shared/utils';
import {
  useDateRangeQueryString,
  useSSOLoginHandler,
  useTokenManagement,
} from '../shared/hooks';
import { AuthContext, SlideDrawerContext } from '../shared/providers';
import { useEntityInformationContextSafe } from '../features/entities/context';
import { AuthenticatedUser } from '../shared/types';
import { AuthState } from '../shared/types/auth-types';
import { setRole } from '@investigator/shared/utils';
import {
  isRefreshTokenError,
  clearCognitoStorage,
  shouldNavigateToLogin,
  shouldSkipAuthCheck,
} from '../shared/utils/authErrorUtils';

const { login, base } = ROUTES;

interface ConditionalRedirectsProps {
  setSearch: (search: string) => void;
  isSSOLogin: boolean;
  setDateRange?: (dateRange?: DateSelectOption | undefined) => void;
  defaultRangeIndex?: number;
  setDefaultRangeIndex?: (index: number) => void;
  setAuthStateLoading: (loading: boolean) => void;
  setTcAcceptanceLoading: (loading: boolean) => void;
}

/**
 * Handles conditional redirects based on authentication state, SSO login flow,
 * and terms acceptance. Manages navigation logic and session persistence.
 *
 * @component
 * @param {ConditionalRedirectsProps} props - Component props
 * @returns {null} This component renders nothing, only handles redirects
 */
export const ConditionalRedirects = ({
  setSearch,
  isSSOLogin,
  setDateRange,
  defaultRangeIndex,
  setDefaultRangeIndex,
  setAuthStateLoading,
  setTcAcceptanceLoading,
}: ConditionalRedirectsProps) => {
  const navigate = useNavigate();

  const { setAuthState, setUser, setUserRole, user, authState } =
    useContext(AuthContext);

  const { saveIdToken, clearTokens } = useTokenManagement();

  const { setIsSlideDrawerOpen } = useContext(SlideDrawerContext) as {
    setIsSlideDrawerOpen?: (open: boolean) => void;
  };
  // Use safe version since ConditionalRedirects is used in both public and protected routes
  const entityContext = useEntityInformationContextSafe();
  const setSelectedEntity = entityContext?.setSelectedEntity;

  const [searchParams] = useSearchParams();

  const routerLocation = useLocation();
  const { search, pathname } = routerLocation;

  const now = moment();

  const { start, end } = useDateRangeQueryString();
  const paramsRange = end - start;

  // Track if initial auth check has been done
  const hasInitializedRef = useRef(false);
  const hasRedirectedRef = useRef(false);
  const eulaCheckInProgressRef = useRef(false);

  const { checkSsoError, getSessionData, idpLogin } = useSSOLoginHandler();

  const [getUserTcAcceptance, { loading: tcAcceptanceLoading }] =
    useGetUserTcAcceptance();

  /**
   * Determines navigation path based on email parameter validity
   * @returns {string} The navigation path
   */
  const getNavPath = useCallback((): string => {
    const paramEmail = searchParams.get('email') ?? '';
    const isEmailValid = REGEX.EMAIL.test(paramEmail);
    return isEmailValid
      ? `/login/trouble/password/reset?${searchParams.toString()}`
      : '/login';
  }, [searchParams]);

  /**
   * Checks if a user (assumed to be SSO) has not accepted the EULA
   * This function should only be called for already-confirmed SSO users
   * @async
   * @param {AuthenticatedUser} authenticatedUser - The authenticated user object
   * @returns {Promise<boolean>} Whether the user has not accepted EULA (is "new")
   */
  const checkIfNewSSOUser = useCallback(async (): Promise<boolean> => {
    if (eulaCheckInProgressRef.current) return false;

    // ✅ Skip EULA check during SSO OAuth callback (code + sso_domain)
    const urlParams = new URLSearchParams(window.location.search);
    const inSSOCallback =
      urlParams.has('code') && !!window.sessionStorage.getItem('sso_domain');
    if (inSSOCallback) {
      return false;
    }

    // Check if we've already confirmed acceptance for this session
    const sessionEulaChecked = sessionStorage.getItem('eula_check_complete');
    if (sessionEulaChecked === 'true') {
      return false;
    }

    eulaCheckInProgressRef.current = true;
    try {
      setTcAcceptanceLoading(true);

      // First check local storage for existing acceptance
      const localEulaAccepted = localStorage.getItem('isEulaAccepted');
      const userScopedEulaKey = user
        ? `accept_privacy_policy_${user.attributes?.email || user.attributes?.sub}`
        : 'accept_privacy_policy';
      const pendingAcceptance =
        localStorage.getItem(userScopedEulaKey) === 'true' ||
        localStorage.getItem('accept_privacy_policy') === 'true';

      // If locally accepted and not pending, skip backend check
      if (localEulaAccepted === 'true' && !pendingAcceptance) {
        sessionStorage.setItem('eula_check_complete', 'true');
        return false;
      }

      // Check with backend
      const { data } = await getUserTcAcceptance();
      const isEulaAccepted = !!data?.getUserTcAcceptance?.body?.data;

      // Update local storage
      localStorage.setItem('isEulaAccepted', String(isEulaAccepted));
      sessionStorage.setItem('eula_check_complete', 'true');

      // Only treat as new user if explicitly pending or never accepted
      return pendingAcceptance || (!isEulaAccepted && !localEulaAccepted);
    } catch (error) {
      console.error(
        '[ConditionalRedirects] Error checking SSO user EULA status:',
        error,
      );
      // On error, assume user is not new to avoid blocking
      sessionStorage.setItem('eula_check_complete', 'true');
      return false;
    } finally {
      setTcAcceptanceLoading(false);
      eulaCheckInProgressRef.current = false;
    }
  }, [getUserTcAcceptance, setTcAcceptanceLoading, user]);

  /**
   * Checks if user is logged in and handles authentication state
   * Manages session validation, SSO detection, and routing
   * @async
   */
  const checkUserLoggedIn = useCallback(async () => {
    try {
      // FIRST: Handle SSO session if returning from OKTA
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('code');
      const isSSOReturn =
        !!authCode && window.sessionStorage.getItem('sso_domain');

      // Show loading state during SSO callback processing
      if (isSSOReturn) {
        setAuthStateLoading(true);
      }

      // isSSOReturn checks and SSO session processing is done
      if (isSSOReturn) {
        const sessionData = await getSessionData();
        if (sessionData) {
          await idpLogin(sessionData);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // Clean the URL so we’re no longer “in callback”
        const url = new URL(window.location.href);
        if (url.searchParams.has('code') || url.searchParams.has('state')) {
          url.searchParams.delete('code');
          url.searchParams.delete('state');
          window.history.replaceState({}, document.title, url.toString());
        }
      }

      // NOW check for authenticated user
      const authenticatedUser: AuthenticatedUser =
        await Auth.currentAuthenticatedUser({
          bypassCache: Boolean(isSSOReturn), // Force refresh for SSO returns
        });

      if (authenticatedUser) {
        // Detect if this is an SSO user
        const isSSOLoginDetected =
          !!authenticatedUser.signInUserSession?.idToken?.payload?.identities ||
          !!window.sessionStorage.getItem('sso_domain');

        window.sessionStorage.setItem('isSSOLogin', String(isSSOLoginDetected));

        // For SSO users, check EULA status
        if (isSSOLoginDetected && !pathname.startsWith('/privacy-policy')) {
          const isNewSSOUser = await checkIfNewSSOUser();

          if (isNewSSOUser) {
            // Store the intended destination BEFORE redirecting
            const next = searchParams.get('next') || pathname;
            if (
              next &&
              next !== '/' &&
              next !== '/login' &&
              next !== '/privacy-policy'
            ) {
              localStorage.setItem('post_eula_redirect', next);
            }

            localStorage.setItem('accept_privacy_policy', 'true');
            localStorage.setItem('humio_tc_accepted', 'true');
            navigate('/privacy-policy', { replace: true });

            // Still set auth state for privacy policy page
            setUser(authenticatedUser);
            setAuthState(AuthState.SignedIn);
            setRole({
              user: {
                ...authenticatedUser,
                challengeName: authenticatedUser.challengeName ?? '',
                challengeParam: authenticatedUser.challengeParam ?? {},
              },
              setUserRole,
            });

            return;
          }
        }

        // Set user state
        setUser(authenticatedUser);
        setAuthState(AuthState.SignedIn);
        setRole({
          user: {
            ...authenticatedUser,
            challengeName: authenticatedUser.challengeName ?? '',
            challengeParam: authenticatedUser.challengeParam ?? {},
          },
          setUserRole,
        });

        // Save token
        const idToken = authenticatedUser.signInUserSession?.idToken?.jwtToken;
        if (idToken) {
          saveIdToken(idToken, authenticatedUser);
        }

        // Handle navigation
        const next = searchParams.get('next');
        const hasError = searchParams.get('error');
        if (pathname.startsWith(login)) {
          if (next) {
            navigate(next, { replace: true });
          } else if (!hasError) {
            // Only redirect to base if there's no error param (error param indicates intentional redirect)
            navigate(base, { replace: true });
          }
        }
      }
    } catch (error) {
      console.error('[ConditionalRedirects] Auth check failed:', error);

      const isRefreshToken = isRefreshTokenError(error, pathname);

      // Aggressive cleanup for refresh token failures
      if (isRefreshToken) {
        clearCognitoStorage();
      }

      // Only redirect to login if appropriate (not in MFA/registration/SSO flows)
      if (shouldNavigateToLogin(pathname)) {
        const navPath = getNavPath();
        const redirectUrl = buildLoginRedirectUrl(pathname + search, navPath);

        // Clear old session (standard cleanup for non-refresh-token errors)
        if (!isRefreshToken && user && authState) {
          clearTokens(user);
          sessionStorage.clear();
          // Clear SSO cookies
          document.cookie =
            'sso_domain=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }

        setUser(undefined);
        setAuthState(AuthState.SignedOut);

        navigate(redirectUrl, { replace: true });
      }
    } finally {
      setAuthStateLoading(false);
    }
  }, [
    getSessionData,
    idpLogin,
    searchParams,
    pathname,
    navigate,
    setUser,
    setAuthState,
    setUserRole,
    getNavPath,
    search,
    setAuthStateLoading,
    user,
    authState,
    saveIdToken,
    clearTokens,
    checkIfNewSSOUser,
  ]);

  /**
   * Handles SSO errors from URL parameters
   * @returns {{ type: string, message: string }} Error type and message
   */
  const handleSSOErrors = useCallback(() => {
    const ssoError = checkSsoError();

    // Check URL parameters for new user indicator
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description') || '';

    // Check if this is a new SSO user based on error parameters
    if (
      errorParam === 'invalid_request' &&
      errorDescription.toLowerCase().includes('no account linked')
    ) {
      return { type: 'newUser', message: '' };
    }

    return ssoError;
  }, [checkSsoError, searchParams]);

  /**
   * Single initialization effect - runs only once on mount
   * Checks SSO errors and initiates authentication flow
   */
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const ssoUserError = handleSSOErrors();

    if (ssoUserError.type === 'newUser') {
      // New SSO user detected from error parameters
      localStorage.setItem('accept_privacy_policy', 'true');
      localStorage.setItem('humio_tc_accepted', 'true');
      navigate('/privacy-policy', { replace: true });
    } else if (ssoUserError.type === 'ssoError') {
      navigate('/sso-error', {
        replace: true,
        state: { message: ssoUserError.message },
      });
    } else {
      // Normal authentication check
      checkUserLoggedIn();
    }
  }, [handleSSOErrors, navigate, checkUserLoggedIn]);

  /**
   * Auth hub listener for handling sign out and token refresh events
   */
  useEffect(() => {
    const authListener = (data: {
      payload: { event: string; data?: unknown };
    }) => {
      switch (data.payload.event) {
        case 'signOut':
          navigate(ROUTES.login, { replace: true });
          break;
        case 'tokenRefresh_failure':
          checkUserLoggedIn();
          break;
      }
    };
    Hub.listen('auth', authListener);
    return () => Hub.remove('auth', authListener);
  }, [checkUserLoggedIn, navigate]);

  /**
   * Handle SSO redirect from localStorage
   */
  useEffect(() => {
    if (hasRedirectedRef.current) return;

    const nextFromLocalStorage = localStorage.getItem('next');
    const isSSOLoginFromStorage = sessionStorage.getItem('isSSOLogin');

    if (
      nextFromLocalStorage &&
      isSSOLoginFromStorage &&
      !pathname.startsWith('/privacy-policy')
    ) {
      hasRedirectedRef.current = true;
      navigate(nextFromLocalStorage, { replace: true });
      localStorage.removeItem('next');
    }
  }, [pathname, navigate]);

  /**
   * Re-check EULA acceptance when returning from privacy policy
   */
  useEffect(() => {
    if (pathname === '/' && isSSOLogin) {
      const pendingEula =
        localStorage.getItem('accept_privacy_policy') === 'true';
      const eulaAccepted = localStorage.getItem('isEulaAccepted') === 'true';

      if (pendingEula && eulaAccepted) {
        // EULA has been accepted, clear flags and proceed
        localStorage.removeItem('accept_privacy_policy');
        localStorage.removeItem('humio_tc_accepted');
      }
    }
  }, [pathname, isSSOLogin]);

  /**
   * Clear session check flag when navigating to login/registration
   */
  useEffect(() => {
    return () => {
      // Clear the check flag if user navigates away to auth pages
      if (
        pathname.startsWith('/login') ||
        pathname.startsWith('/registration')
      ) {
        sessionStorage.removeItem('eula_check_complete');
      }
    };
  }, [pathname]);

  /**
   * Date range setup
   */
  useEffect(() => {
    if (setDateRange && dateRangeOptions[defaultRangeIndex ?? 0]) {
      setDateRange(dateRangeOptions[defaultRangeIndex ?? 0]);
    }
  }, [defaultRangeIndex, setDateRange]);

  /**
   * Date range index calculation based on URL parameters
   */
  useEffect(() => {
    if (start && end && setDefaultRangeIndex) {
      let optionIndex = 0;
      let lowest = Infinity;
      dateRangeOptions.forEach(([amount, type], index) => {
        const optionRange = now.unix() - now.subtract(amount, type).unix();
        const diff = Math.abs(paramsRange - optionRange);
        if (diff < lowest) {
          optionIndex = index;
          lowest = diff;
        }
      });
      setDefaultRangeIndex(optionIndex);
    }
  }, [paramsRange, now, start, end, setDefaultRangeIndex]);

  /**
   * Search params sync
   */
  useEffect(() => setSearch(search), [search, setSearch]);

  /**
   * TC acceptance loading sync
   */
  useEffect(
    () => setTcAcceptanceLoading(tcAcceptanceLoading),
    [tcAcceptanceLoading, setTcAcceptanceLoading],
  );

  /**
   * Clear slide drawer on navigation
   */
  useEffect(() => {
    setIsSlideDrawerOpen?.(false);
    setSelectedEntity?.(undefined);
  }, [pathname, setIsSlideDrawerOpen, setSelectedEntity]);

  /**
   * Storage event listener for cross-tab logout
   */
  useEffect(() => {
    const handleTokenChange = (event: StorageEvent) => {
      if (event.key === 'docs_id_token' && event.newValue === null) {
        setUser?.(undefined);
        setAuthState?.(undefined);
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace(ROUTES.login);
      }
    };
    window.addEventListener('storage', handleTokenChange);
    return () => window.removeEventListener('storage', handleTokenChange);
  }, [setUser, setAuthState]);

  // Always check authentication on route change to prevent back navigation to protected routes after logout
  useEffect(() => {
    const timeout = setTimeout(() => {
      // Skip auth check for public pages or when not needed
      if (
        shouldSkipAuthCheck(
          pathname,
          Boolean(user && authState === AuthState.SignedIn),
        )
      ) {
        return;
      }

      checkUserLoggedIn();
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
};
